import styled from 'styled-components';

// Style-only props must not reach the rendered element. This matters when a
// caller passes `as={Link}`: styled-components forwards every prop to a custom
// component, so without this filter `variant`/`active`/... would land on the
// <a> as unknown DOM attributes and React would warn.
const STYLE_ONLY_PROPS = ['variant', 'active', 'elevated', 'primary', 'danger'];

export const StyledButton = styled.button.withConfig({
  shouldForwardProp: (prop, defaultValidatorFn, target) =>
    !STYLE_ONLY_PROPS.includes(prop) &&
    (typeof target !== 'string' || defaultValidatorFn(prop)),
})`
  /* Base styles */
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  font-weight: 500;
  position: relative;
  border: 1px solid;

  /* Variant: default (action button style) */
  ${(props) =>
    !props.variant || props.variant === 'default'
      ? `
    width: 100%;
    padding: 12px 16px;
    border-radius: 8px;
    font-size: 14px;
    gap: 10px;
    box-shadow: ${
      props.elevated && props.theme.name === 'light'
        ? '0 2px 8px rgba(0, 0, 0, 0.08)'
        : '0 1px 2px rgba(0, 0, 0, 0.05)'
    };
  `
      : ''}

  /* Variant: pill */
  ${(props) =>
    props.variant === 'pill'
      ? `
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 14px;
    gap: 8px;
    white-space: nowrap;
    flex-shrink: 0;
  `
      : ''}

  /* Variant: icon */
  ${(props) =>
    props.variant === 'icon'
      ? `
    width: 32px;
    height: 32px;
    padding: 0;
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  `
      : ''}

  /* Background color */
  background: ${(props) => {
    if (props.primary) {
      return props.theme?.colors?.primary || '#FA4616';
    }
    if (props.active) {
      return 'rgba(250,70,22,0.08)';
    }
    return props.theme.name === 'dark'
      ? 'rgba(255,255,255,0.05)'
      : props.variant === 'pill'
      ? 'rgba(255,255,255,1)'
      : 'white';
  }};

  /* Border color */
  border-color: ${(props) => {
    if (props.primary) {
      return props.theme?.colors?.primary || '#FA4616';
    }
    if (props.danger) {
      return props.theme?.colors?.status?.error || '#EF4444';
    }
    if (props.active) {
      return props.theme?.colors?.primary || '#FA4616';
    }
    // Elevated buttons get stronger border in light theme
    if (props.elevated && props.theme.name === 'light') {
      return props.theme?.colors?.card?.border || 'rgb(226, 232, 240)';
    }
    return props.theme.name === 'dark'
      ? props.theme?.colors?.['border-button'] || 'rgba(255,255,255,0.15)'
      : props.variant === 'pill'
      ? 'rgba(0,0,0,0.15)'
      : props.theme?.colors?.['border-input'] || '#94a3b8';
  }};

  /* Text color */
  color: ${(props) => {
    if (props.primary) {
      return 'white';
    }
    if (props.danger) {
      return props.theme?.colors?.status?.error || '#EF4444';
    }
    if (props.active) {
      return props.theme?.colors?.primary || '#FA4616';
    }
    // Elevated buttons get darker text in light theme
    if (props.elevated && props.theme.name === 'light') {
      return props.theme?.colors?.textGray?.dark || '#334155';
    }
    // For pill variant, use brighter text in dark mode for better visibility
    if (props.variant === 'pill' && props.theme.name === 'dark') {
      return (
        props.theme?.colors?.text?.secondary || 'rgba(255, 255, 255, 0.85)'
      );
    }
    return props.theme.name === 'dark'
      ? props.theme?.colors?.textGray?.secondary || '#94a3b8'
      : props.theme?.colors?.text?.secondary || '#475569';
  }};

  /* Icon sizing */
  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    pointer-events: none;
  }

  /* Hover state */
  &:hover:not(:disabled) {
    ${(props) => {
      // Primary button hover
      if (props.primary) {
        return `
      background: #e03e0c;
      border-color: #e03e0c;
    `;
      }
      // Danger button hover
      if (props.danger) {
        return `
      background: ${
        props.theme.name === 'dark'
          ? 'rgba(239, 68, 68, 0.15)'
          : 'rgba(239, 68, 68, 0.08)'
      };
      border-color: ${props.theme?.colors?.status?.error || '#EF4444'};
      color: ${props.theme?.colors?.status?.error || '#EF4444'};

      svg {
        color: ${props.theme?.colors?.status?.error || '#EF4444'};
      }
    `;
      }
      // Regular button hover (not active)
      if (!props.active) {
        return `
      background: ${
        props.theme.name === 'dark'
          ? 'rgba(250, 70, 22, 0.15)'
          : 'rgba(250,70,22,0.05)'
      };
      border-color: ${props.theme?.colors?.primary || '#fa4616'};
      color: ${props.theme?.colors?.primary || '#fa4616'};

      svg {
        color: ${props.theme?.colors?.primary || '#fa4616'};
      }
    `;
      }
      return '';
    }}
  }

  /* Disabled state */
  &:disabled {
    cursor: not-allowed;
    opacity: ${(props) => (props.theme.name === 'dark' ? 0.5 : 1)};
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.05)'
        : 'rgba(0,0,0,0.03)'} !important;
    border-color: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : 'rgba(0,0,0,0.15)'} !important;
    color: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.3)'
        : props.theme?.colors?.textGray?.disabled || '#9CA3AF'} !important;

    svg {
      color: ${(props) =>
        props.theme.name === 'dark'
          ? 'rgba(255,255,255,0.3)'
          : props.theme?.colors?.textGray?.disabled || '#9CA3AF'} !important;
    }
  }
`;
